import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Tabs from "@mui/joy/Tabs";
import TabList from "@mui/joy/TabList";
import Tab, { tabClasses } from "@mui/joy/Tab";
import TabPanel from "@mui/joy/TabPanel";
import { useLocation, useNavigate } from "react-router-dom";
import { Game } from "../../models/games";
import { gamesListApi } from "../games/gamesApi";
import { NotificationService } from "../../common/notification";
import { useUeFilters } from "./components/UeFilters";
import { OverviewTab } from "./tabs/OverviewTab";
import {
    ActivationFunnelTab,
    LtvTab,
    ParetoTab,
    RepeatRateTab,
    RetentionTab,
    TimeToValueTab,
} from "./tabs/UsersTabs";
import { DepositQualityTab, NetFlowTab, PaymentTakeRateTab } from "./tabs/PaymentsTabs";
import { GmvByGameTab, MarketplaceRolesTab, SellerBuyerTab } from "./tabs/MarketTabs";
import { CampaignsTab } from "./tabs/CampaignsTab";
import { TermHint } from "./components/TermHint";

const TOP_SLUGS = ["ue-overview", "ue-users", "ue-payments", "ue-market", "ue-campaigns"] as const;

const USER_SLUGS = [
    "ue-users-funnel",
    "ue-users-speed",
    "ue-users-retention",
    "ue-users-ltv",
    "ue-users-repeat",
    "ue-users-pareto",
] as const;

const PAYMENT_SLUGS = ["ue-payments-methods", "ue-payments-float", "ue-payments-quality"] as const;

const MARKET_SLUGS = ["ue-market-games", "ue-market-sides", "ue-market-roles"] as const;

function normalizeHash(hash: string): string {
    return (hash || "").replace("#", "").toLowerCase();
}

function parseHash(hash: string) {
    const slug = normalizeHash(hash) || TOP_SLUGS[0];
    let top = 0;
    let inner = 0;

    if (slug.startsWith("ue-users")) {
        top = 1;
        const idx = USER_SLUGS.indexOf(slug as (typeof USER_SLUGS)[number]);
        inner = idx >= 0 ? idx : 0;
    } else if (slug.startsWith("ue-payments")) {
        top = 2;
        const idx = PAYMENT_SLUGS.indexOf(slug as (typeof PAYMENT_SLUGS)[number]);
        inner = idx >= 0 ? idx : 0;
    } else if (slug.startsWith("ue-market")) {
        top = 3;
        const idx = MARKET_SLUGS.indexOf(slug as (typeof MARKET_SLUGS)[number]);
        inner = idx >= 0 ? idx : 0;
    } else if (slug === "ue-campaigns" || slug.startsWith("ue-campaigns")) {
        top = 4;
        inner = 0;
    } else {
        top = 0;
        inner = 0;
    }

    return { top, inner };
}

const tabListSx = {
    pl: { xs: 0, md: 2 },
    justifyContent: "left",
    [`&& .${tabClasses.root}`]: {
        fontWeight: "600",
        flex: "initial",
        color: "text.tertiary",
        [`&.${tabClasses.selected}`]: {
            bgcolor: "transparent",
            color: "text.primary",
            "&::after": {
                height: "2px",
                bgcolor: "primary.500",
            },
        },
    },
} as const;

export function UnitEconomics() {
    const navigate = useNavigate();
    const location = useLocation();
    const [{ top, inner }, setIndices] = React.useState(() => parseHash(location.hash));
    const [filters, setFilters] = useUeFilters();
    const [games, setGames] = React.useState<Game[]>([]);
    const [gamesLoading, setGamesLoading] = React.useState(true);

    React.useEffect(() => {
        setIndices(parseHash(location.hash));
    }, [location.hash]);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await gamesListApi();
                if (cancelled) return;
                if (result.result) {
                    setGames(result.value ?? []);
                } else {
                    NotificationService.alertError("Не удалось загрузить список игр.");
                }
            } finally {
                if (!cancelled) setGamesLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const goHash = (slug: string) => {
        const next = `#${slug}`;
        if (normalizeHash(location.hash) !== slug) {
            navigate(
                { pathname: location.pathname, search: location.search, hash: next },
                { replace: false }
            );
        }
    };

    const handleTopChange = (_: React.SyntheticEvent | null, value: number | string | null) => {
        const idx = typeof value === "number" ? value : 0;
        if (idx === 1) {
            goHash(USER_SLUGS[0]);
            return;
        }
        if (idx === 2) {
            goHash(PAYMENT_SLUGS[0]);
            return;
        }
        if (idx === 3) {
            goHash(MARKET_SLUGS[0]);
            return;
        }
        if (idx === 4) {
            goHash("ue-campaigns");
            return;
        }
        goHash(TOP_SLUGS[0]);
    };

    const common = {
        filters,
        onFiltersChange: setFilters,
    };

    return (
        <Box
            component="main"
            className="MainContent"
            sx={{
                px: { xs: 2, md: 6 },
                pt: {
                    xs: "calc(12px + var(--Header-height))",
                    sm: "calc(12px + var(--Header-height))",
                    md: 3,
                },
                pb: { xs: 2, sm: 2, md: 3 },
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                height: "100dvh",
                gap: 1,
            }}
        >
            <Typography level="h2" component="h1" sx={{ mb: 1 }}>
                <TermHint term="unitEconomics" iconSize={18}>
                    Юнит-экономика
                </TermHint>
            </Typography>

            <Tabs
                value={top}
                onChange={handleTopChange}
                sx={{ bgcolor: "transparent", flex: 1, display: "flex", flexDirection: "column" }}
            >
                <TabList size="sm" tabFlex={1} sx={tabListSx}>
                    <Tab sx={{ borderRadius: "6px 6px 0 0" }} indicatorInset value={0}>
                        Обзор
                    </Tab>
                    <Tab sx={{ borderRadius: "6px 6px 0 0" }} indicatorInset value={1}>
                        Пользователи
                    </Tab>
                    <Tab sx={{ borderRadius: "6px 6px 0 0" }} indicatorInset value={2}>
                        Платежи и float
                    </Tab>
                    <Tab sx={{ borderRadius: "6px 6px 0 0" }} indicatorInset value={3}>
                        Маркетплейс
                    </Tab>
                    <Tab sx={{ borderRadius: "6px 6px 0 0" }} indicatorInset value={4}>
                        Реклама
                    </Tab>
                </TabList>

                <TabPanel value={0} sx={{ p: 0, pt: 1.5 }}>
                    <OverviewTab isActive={top === 0} {...common} />
                </TabPanel>

                <TabPanel value={1} sx={{ p: 0, pt: 1 }}>
                    <Tabs
                        value={inner}
                        onChange={(_, v) => goHash(USER_SLUGS[typeof v === "number" ? v : 0] || USER_SLUGS[0])}
                        sx={{ bgcolor: "transparent" }}
                    >
                        <TabList size="sm" sx={tabListSx}>
                            <Tab value={0}>
                                <TermHint term="funnel">Воронка</TermHint>
                            </Tab>
                            <Tab value={1}>Скорость</Tab>
                            <Tab value={2}>
                                <TermHint term="retention" />
                            </Tab>
                            <Tab value={3}>
                                <TermHint term="ltv" />
                            </Tab>
                            <Tab value={4}>
                                <TermHint term="repeat">Повтор</TermHint>
                            </Tab>
                            <Tab value={5}>
                                <TermHint term="pareto" />
                            </Tab>
                        </TabList>
                        <TabPanel value={0} sx={{ p: 0, pt: 1.5 }}>
                            <ActivationFunnelTab isActive={top === 1 && inner === 0} {...common} />
                        </TabPanel>
                        <TabPanel value={1} sx={{ p: 0, pt: 1.5 }}>
                            <TimeToValueTab isActive={top === 1 && inner === 1} {...common} />
                        </TabPanel>
                        <TabPanel value={2} sx={{ p: 0, pt: 1.5 }}>
                            <RetentionTab isActive={top === 1 && inner === 2} {...common} />
                        </TabPanel>
                        <TabPanel value={3} sx={{ p: 0, pt: 1.5 }}>
                            <LtvTab isActive={top === 1 && inner === 3} {...common} />
                        </TabPanel>
                        <TabPanel value={4} sx={{ p: 0, pt: 1.5 }}>
                            <RepeatRateTab isActive={top === 1 && inner === 4} {...common} />
                        </TabPanel>
                        <TabPanel value={5} sx={{ p: 0, pt: 1.5 }}>
                            <ParetoTab isActive={top === 1 && inner === 5} {...common} />
                        </TabPanel>
                    </Tabs>
                </TabPanel>

                <TabPanel value={2} sx={{ p: 0, pt: 1 }}>
                    <Tabs
                        value={Math.min(inner, PAYMENT_SLUGS.length - 1)}
                        onChange={(_, v) =>
                            goHash(PAYMENT_SLUGS[typeof v === "number" ? v : 0] || PAYMENT_SLUGS[0])
                        }
                        sx={{ bgcolor: "transparent" }}
                    >
                        <TabList size="sm" sx={tabListSx}>
                            <Tab value={0}>Методы</Tab>
                            <Tab value={1}>
                                <TermHint term="float" />
                            </Tab>
                            <Tab value={2}>Качество</Tab>
                        </TabList>
                        <TabPanel value={0} sx={{ p: 0, pt: 1.5 }}>
                            <PaymentTakeRateTab isActive={top === 2 && inner === 0} {...common} />
                        </TabPanel>
                        <TabPanel value={1} sx={{ p: 0, pt: 1.5 }}>
                            <NetFlowTab isActive={top === 2 && inner === 1} {...common} />
                        </TabPanel>
                        <TabPanel value={2} sx={{ p: 0, pt: 1.5 }}>
                            <DepositQualityTab isActive={top === 2 && inner === 2} {...common} />
                        </TabPanel>
                    </Tabs>
                </TabPanel>

                <TabPanel value={3} sx={{ p: 0, pt: 1 }}>
                    <Tabs
                        value={Math.min(inner, MARKET_SLUGS.length - 1)}
                        onChange={(_, v) =>
                            goHash(MARKET_SLUGS[typeof v === "number" ? v : 0] || MARKET_SLUGS[0])
                        }
                        sx={{ bgcolor: "transparent" }}
                    >
                        <TabList size="sm" sx={tabListSx}>
                            <Tab value={0}>По играм</Tab>
                            <Tab value={1}>Стороны</Tab>
                            <Tab value={2}>Роли</Tab>
                        </TabList>
                        <TabPanel value={0} sx={{ p: 0, pt: 1.5 }}>
                            <GmvByGameTab
                                isActive={top === 3 && inner === 0}
                                games={games}
                                gamesLoading={gamesLoading}
                                {...common}
                            />
                        </TabPanel>
                        <TabPanel value={1} sx={{ p: 0, pt: 1.5 }}>
                            <SellerBuyerTab
                                isActive={top === 3 && inner === 1}
                                games={games}
                                gamesLoading={gamesLoading}
                                {...common}
                            />
                        </TabPanel>
                        <TabPanel value={2} sx={{ p: 0, pt: 1.5 }}>
                            <MarketplaceRolesTab
                                isActive={top === 3 && inner === 2}
                                games={games}
                                gamesLoading={gamesLoading}
                                {...common}
                            />
                        </TabPanel>
                    </Tabs>
                </TabPanel>

                <TabPanel value={4} sx={{ p: 0, pt: 1.5 }}>
                    <CampaignsTab isActive={top === 4} />
                </TabPanel>
            </Tabs>
        </Box>
    );
}

export default UnitEconomics;
